import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { SubscriptionHandlerComponent } from './components/misc/subscription-handler/subscription-handler.component';
import { ErrorHandlerService } from './services/error-handler.service';
import { InitService } from './services/init.service';
import { BasePageComponent } from './components/pages/base-page/base-page.component';
import { LoadingOverlayComponent } from './components/misc/loading-overlay/loading-overlay.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { LoginPageComponent } from './components/pages/login-page/login-page.component';
import { BaseFormComponent } from './components/forms/base-form/base-form.component';
import { LoginFormComponent } from './components/forms/login-form/login-form.component';
import { ButtonLoadingDirective } from './directives/button-loading.directive';
import { DispatcherPageComponent } from './components/pages/dispatcher-page/dispatcher-page.component';
import { LandingPageComponent } from './components/pages/landing-page/landing-page.component';
import { TabsHeaderComponent } from './components/misc/tabs-header/tabs-header.component';
import { LogoComponent } from './components/misc/logo/logo.component';
import { ChartComponent } from './components/misc/chart/chart.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { InfoCardComponent } from './components/misc/info-card/info-card.component';
import { BaseModalComponent } from './components/modals/base-modal/base-modal.component';
import { ChartSettingsModalComponent } from './components/modals/chart-settings-modal/chart-settings-modal.component';
import { ChartSettingsFormComponent } from './components/forms/chart-settings-form/chart-settings-form.component';
import { GroupedChartComponent } from './components/misc/grouped-chart/grouped-chart.component';
import { FooterComponent } from './components/misc/footer/footer.component';
import { LandingSettingsModalComponent } from './components/modals/landing-settings-modal/landing-settings-modal.component';
import { LandingSettingsFormComponent } from './components/forms/landing-settings-form/landing-settings-form.component';
import { SortChartDataPipe } from './pipes/sort-chart-data.pipe';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { DateRangeFormComponent } from './components/forms/date-range-form/date-range-form.component';
import { DateRangeModalComponent } from './components/modals/date-range-modal/date-range-modal.component';
import { FilterPipe } from './pipes/filter.pipe';
import { AccumulatedChartValuePipe } from './pipes/accumulated-chart-value.pipe';
import { NgxGraphModule } from '@swimlane/ngx-graph';
import { FlowGraphComponent } from './components/misc/flow-graph/flow-graph.component';
import { ActionDateRangePipe } from './pipes/action-date-range.pipe';
import { ActiveActionPipe } from './pipes/active-action.pipe';
import { StatCardComponent } from './components/misc/stat-card/stat-card.component';
import { AddObjectModalComponent } from './components/modals/add-object-modal/add-object-modal.component';
import { CloudConfigurationModalComponent } from './components/modals/cloud-configuration-modal/cloud-configuration-modal.component';
import { CloudConfigurationFormComponent } from './components/forms/cloud-configuration-form/cloud-configuration-form.component';

const DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [
    AppComponent,
    SubscriptionHandlerComponent,
    BasePageComponent,
    LoadingOverlayComponent,
    LoginPageComponent,
    BaseFormComponent,
    LoginFormComponent,
    ButtonLoadingDirective,
    DispatcherPageComponent,
    LandingPageComponent,
    TabsHeaderComponent,
    LogoComponent,
    ChartComponent,
    InfoCardComponent,
    BaseModalComponent,
    ChartSettingsModalComponent,
    ChartSettingsFormComponent,
    GroupedChartComponent,
    FooterComponent,
    LandingSettingsModalComponent,
    LandingSettingsFormComponent,
    SortChartDataPipe,
    DateRangeFormComponent,
    DateRangeModalComponent,
    FilterPipe,
    AccumulatedChartValuePipe,
    FlowGraphComponent,
    ActionDateRangePipe,
    ActiveActionPipe,
    StatCardComponent,
    AddObjectModalComponent,
    CloudConfigurationModalComponent,
    CloudConfigurationFormComponent,
  ],
  imports: [
    NgxGraphModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatMomentDateModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatRadioModule,
    MatSelectModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatSidenavModule,
    MatMenuModule,
    MatTableModule,
    MatPaginatorModule,
    MatRadioModule,
    MatChipsModule,
    MatDialogModule,
    MatTabsModule,
    NgxChartsModule,
    NgxPermissionsModule.forRoot(),
    TranslateModule.forRoot({ defaultLanguage: 'sk' }),
  ],
  providers: [
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { hasBackdrop: true, disableClose: true, minWidth: '350px' },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (initService: InitService) => () => initService.init(),
      deps: [InitService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
